import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { newsletterAPI, settingsAPI } from '../../utils/api';
import toast from 'react-hot-toast';

// Helper: only allow http(s):// links so a dashboard typo can't smuggle in a
// `javascript:` URL. Anything else is treated as missing.
const safeUrl = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // accept bare domain like "facebook.com/qayakw" — auto-prefix https
  if (/^[\w.-]+\.[a-z]{2,}/i.test(trimmed)) return `https://${trimmed}`;
  return '';
};

// Strip everything except digits for wa.me — no plus, no spaces, no punctuation.
const waNumber = (raw) => (raw || '').replace(/[^\d]/g, '');

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const { t } = useTranslation();

  useEffect(() => {
    settingsAPI.getPublic()
      .then(rows => {
        const map = Object.fromEntries((rows || []).map(s => [s.setting_key, s.setting_value]));
        setSettings(map);
      })
      .catch(() => setSettings({}));
  }, []);

  // WhatsApp + social hrefs (computed once per render). All optional —
  // the icon hides if the corresponding setting is empty.
  const waDigits = waNumber(settings.contact_whatsapp);
  const waHref = waDigits ? `https://wa.me/${waDigits}` : '';
  const instagramHref = safeUrl(settings.social_instagram || settings.instagram_url);
  const facebookHref = safeUrl(settings.social_facebook);
  const tiktokHref = safeUrl(settings.social_tiktok);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await newsletterAPI.subscribe(email);
      toast.success(t('footer.subscribeSuccess'));
      setEmail('');
    } catch {
      toast.error(t('footer.subscribeFail'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__grid">
          {/* Brand */}
          <div className="footer__brand">
            <img src="/logo.svg" alt="Qaya KW" style={{ height: 72, width: 'auto', marginBottom: 16, display: 'block', background: 'transparent' }} />
            <p>{t('footer.tagline')}</p>
            <div className="footer__social">
              {instagramHref && (
                <a href={instagramHref} target="_blank" rel="noreferrer" aria-label="Instagram">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                  </svg>
                </a>
              )}
              {facebookHref && (
                <a href={facebookHref} target="_blank" rel="noreferrer" aria-label="Facebook">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
                  </svg>
                </a>
              )}
              {tiktokHref && (
                <a href={tiktokHref} target="_blank" rel="noreferrer" aria-label="TikTok">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.69a8.16 8.16 0 0 0 4.77 1.52V6.78a4.85 4.85 0 0 1-1.84-.09Z"/>
                  </svg>
                </a>
              )}
              {waHref && (
                <a href={waHref} target="_blank" rel="noreferrer" aria-label="WhatsApp">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </a>
              )}
            </div>
          </div>

          {/* Links */}
          <div className="footer__col">
            <h4>{t('footer.shop')}</h4>
            <ul>
              <li><Link to="/shop?category=raw-honey">{t('nav.rawHoney')}</Link></li>
              <li><Link to="/shop?category=honeycomb">{t('nav.infusedHoney')}</Link></li>
            </ul>
          </div>

          <div className="footer__col">
            <h4>{t('footer.info')}</h4>
            <ul>
              <li><Link to="/about">{t('footer.ourStory')}</Link></li>
              <li><Link to="/contact">{t('footer.contactUs')}</Link></li>
              <li><Link to="/faq">{t('footer.faq')}</Link></li>
              <li><Link to="/shipping">{t('footer.shipping')}</Link></li>
              <li><Link to="/privacy">{t('footer.privacy')}</Link></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="footer__col">
            <h4>{t('footer.joinHive')}</h4>
            <p style={{ marginBottom: 16 }}>{t('footer.newsletterDesc')}</p>
            <form onSubmit={handleSubscribe} className="footer__newsletter">
              <input
                type="email" placeholder={t('footer.emailPlaceholder')} value={email}
                onChange={e => setEmail(e.target.value)} required
              />
              <button type="submit" disabled={loading} className="btn btn-primary">
                {loading ? '...' : t('footer.subscribe')}
              </button>
            </form>
            <div className="footer__payment-icons">
              <span>KNET</span><span>VISA</span><span>Mastercard</span><span>Apple Pay</span>
            </div>
          </div>
        </div>

        <div className="footer__bottom">
          <p>© {new Date().getFullYear()} Qaya KW. {t('footer.rights')}</p>
          <p>{t('footer.poweredBy')} <a href="https://teknulugy.com" target="_blank" rel="noopener noreferrer" className="footer__powered">Teknulugy</a></p>
        </div>
      </div>

      <style>{`
        .footer { background: var(--olive-dark); color: rgba(255,255,255,0.85); padding: 70px 0 30px; }
        .footer__grid { display: grid; grid-template-columns: 1.5fr 1fr 1fr 1.5fr; gap: 48px; margin-bottom: 48px; }
        .footer__brand p { font-size: 0.88rem; line-height: 1.7; opacity: 0.8; max-width: 260px; }
        .footer__social { display: flex; gap: 14px; margin-top: 16px; }
        .footer__social a { color: rgba(255,255,255,0.7); transition: color 0.2s; }
        .footer__social a:hover { color: var(--gold-light); }
        .footer__col h4 { font-family: var(--font-serif); font-size: 1.1rem; color: var(--gold-light); margin-bottom: 16px; }
        .footer__col ul { list-style: none; display: flex; flex-direction: column; gap: 10px; }
        .footer__col ul li a { font-size: 0.88rem; opacity: 0.8; transition: opacity 0.2s; }
        .footer__col ul li a:hover { opacity: 1; color: var(--gold-light); }
        .footer__newsletter { display: flex; gap: 8px; }
        .footer__newsletter input {
          flex: 1; padding: 10px 14px; border-radius: 50px; border: 1px solid rgba(255,255,255,0.2);
          background: rgba(255,255,255,0.1); color: white; font-size: 0.85rem;
        }
        .footer__newsletter input::placeholder { opacity: 0.6; }
        .footer__newsletter input:focus { outline: none; border-color: var(--gold-light); }
        .footer__newsletter .btn { padding: 10px 18px; font-size: 0.85rem; }
        .footer__payment-icons {
          display: flex; gap: 8px; margin-top: 16px; flex-wrap: wrap;
        }
        .footer__payment-icons span {
          padding: 4px 10px; background: rgba(255,255,255,0.1); border-radius: 6px;
          font-size: 0.72rem; font-weight: 600; letter-spacing: 0.5px;
        }
        .footer__bottom {
          border-top: 1px solid rgba(255,255,255,0.1); padding-top: 24px;
          display: flex; justify-content: space-between; font-size: 0.8rem; opacity: 0.6;
        }
        .footer__powered {
          color: inherit; text-decoration: none; font-weight: 600; transition: opacity 0.2s;
        }
        .footer__powered:hover { opacity: 0.7; text-decoration: underline; }
        @media (max-width: 1024px) { .footer__grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 600px) {
          .footer__grid { grid-template-columns: 1fr; }
          .footer__bottom { flex-direction: column; gap: 8px; }
        }
      `}</style>
    </footer>
  );
};

export default Footer;
